// Менюто на „Складова база": стрелка назад, марка, „Табло" със сгъваемата група
// „Операции" под него, секциите Справки / Настройки със значки (виж
// shell/inventoryNav.ts) и профилната лента. Групата помни отворена/свита
// (`useNavGroupOpen`, свита по подразбиране).
// Широк екран — 248px; таблет — рейл 68px (само икони); под 640px — чекмедже
// над съдържанието. Всичко е Tailwind; hover е само с `hover:` (JS
// onMouseEnter „залепва" на телефон).
import { Tooltip } from "antd";
import {
  LeftOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../../store/hooks";
import { INVENTORY_NAV, navBadgeToneOf } from "../shell/inventoryNav";
import type { SidebarBadges } from "../shell/useSidebarBadges";
import { useNavGroupOpen } from "../shell/useNavGroupOpen";
import { SidebarNavItem } from "./SidebarNavItem";
import { SidebarNavGroup } from "./SidebarNavGroup";
import { SidebarProfileBar } from "./SidebarProfileBar";

type Props = {
  collapsed: boolean;
  onToggle: () => void;
  activeSlug: string;
  onNavigate: (slug: string) => void;
  badges: SidebarBadges;
  isNarrow: boolean;
  mobileOpen: boolean;
  onMobileClose: () => void;
  backLabel?: string;
  onBack: () => void;
};

export const InventorySidebar = ({
  collapsed,
  onToggle,
  activeSlug,
  onNavigate,
  badges,
  isNarrow,
  mobileOpen,
  onMobileClose,
  backLabel,
  onBack,
}: Props) => {
  const { t } = useTranslation();
  const access = useAppSelector((s) => s.auth.access);
  // В чекмеджето менюто е винаги разгънато
  const isCollapsed = isNarrow ? false : collapsed;
  const backText = backLabel ?? t("inventoryPage.backToModules");
  // Единствената група в менюто — операциите под „Табло"
  const operations = useNavGroupOpen("inventory", "operations");

  if (isNarrow && !mobileOpen) return null;

  return (
    <aside
      className={`flex shrink-0 flex-col border-r border-shade-2 bg-sidebar px-3 py-4 font-pos-sans text-body ${
        isNarrow
          ? "dk-elevated fixed inset-y-0 left-0 z-50 w-[280px] shadow-[0_10px_30px_rgba(17,17,17,0.2)]"
          : `relative transition-[width] duration-[180ms] ${
              isCollapsed ? "w-[68px]" : "w-[248px]"
            }`
      }`}
    >
      {/* Назад (към модулите или от работен лист към списъка) */}
      <div className="mb-3.5 flex items-center justify-between">
        {isCollapsed ? (
          <Tooltip title={backText} placement="right">
            <button
              type="button"
              onClick={onBack}
              aria-label={backText}
              className="flex w-full cursor-pointer items-center justify-center border-none bg-transparent py-1.5 text-body hover:text-ink"
            >
              <LeftOutlined className="text-[13px]" />
            </button>
          </Tooltip>
        ) : (
          <button
            type="button"
            onClick={onBack}
            className="flex cursor-pointer items-center gap-2 rounded-md border-none bg-transparent px-2.5 py-1.5 text-left text-xs font-medium text-body hover:text-ink"
          >
            <LeftOutlined className="text-[11px]" />
            {backText}
          </button>
        )}
        {isNarrow && (
          <button
            type="button"
            onClick={onMobileClose}
            aria-label={t("inventoryPage.closeMenu")}
            className="cursor-pointer border-none bg-transparent p-1.5 text-base leading-none text-body"
          >
            ✕
          </button>
        )}
      </div>

      {/* Марка */}
      <div
        className={`flex items-center gap-2.5 ${
          isCollapsed ? "justify-center pb-3 pt-1" : "px-2.5 py-1"
        }`}
      >
        <img src="/logo-ts.png" alt="UnrealSoft" className="h-8 w-8 object-contain" />
        {!isCollapsed && (
          <div className="min-w-0 leading-[1.2]">
            <div
              className={`text-[15px] font-semibold tracking-[-0.1px] ${
                access ? "text-brand-deep-ink" : "text-ink"
              }`}
            >
              UnrealSoft
            </div>
            <div className="mt-0.5 whitespace-nowrap text-xs text-muted">
              {t("inventoryPage.moduleLabel")}
            </div>
          </div>
        )}
      </div>

      <div
        className={`h-px bg-shade-2 ${isCollapsed ? "mx-2 my-3" : "mx-0.5 my-3.5"}`}
      />

      {/* Секциите */}
      <nav className="flex-1 overflow-y-auto" aria-label={t("inventoryPage.moduleLabel")}>
        {INVENTORY_NAV.map((section, index) => (
          <div key={section.titleKey ?? "top"} className={index === 0 ? "" : "mt-4"}>
            {isCollapsed
              ? index > 0 && <div className="mx-2 mb-2.5 h-px bg-shade-2" />
              : section.titleKey && (
                  <div className="px-2.5 pb-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-muted">
                    {t(section.titleKey)}
                  </div>
                )}
            {section.items.map((item) => (
              <SidebarNavItem
                key={item.slug}
                item={item}
                label={t(item.labelKey)}
                active={activeSlug === item.slug}
                collapsed={isCollapsed}
                badge={item.badge ? badges[item.badge] : undefined}
                badgeTone={item.badge ? navBadgeToneOf(item.badge, badges) : null}
                onClick={() => onNavigate(item.slug)}
              />
            ))}
            {section.group && (
              <SidebarNavGroup
                group={section.group}
                open={operations.open}
                onToggle={operations.toggle}
                activeSlug={activeSlug}
                collapsed={isCollapsed}
                badges={badges}
                onNavigate={onNavigate}
              />
            )}
          </div>
        ))}
      </nav>

      <SidebarProfileBar collapsed={isCollapsed} />

      {/* Свий / разпъни (не и в чекмеджето) */}
      {!isNarrow && (
        <div className="mt-2 border-t border-shade-2 pt-2.5">
          <Tooltip
            title={
              isCollapsed
                ? t("inventoryPage.expandMenu")
                : t("inventoryPage.collapseMenu")
            }
            placement="right"
          >
            <button
              type="button"
              onClick={onToggle}
              aria-label={
                isCollapsed
                  ? t("inventoryPage.expandMenu")
                  : t("inventoryPage.collapseMenu")
              }
              className={`flex w-full cursor-pointer items-center gap-2.5 rounded-lg border-none bg-transparent py-[9px] text-xs font-medium text-muted transition-colors hover:bg-shade-1 hover:text-ink ${
                isCollapsed ? "justify-center" : "px-2.5"
              }`}
            >
              {isCollapsed ? (
                <MenuUnfoldOutlined className="text-[15px]" />
              ) : (
                <>
                  <MenuFoldOutlined className="text-[15px]" />
                  <span>{t("inventoryPage.collapse")}</span>
                </>
              )}
            </button>
          </Tooltip>
        </div>
      )}
    </aside>
  );
};

export default InventorySidebar;
